import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from '../local-storage/local-storage.service';
import {ROLE_PIGLET_LOGIN, UserInfo} from "../../models/user/user-info.model";
import { LoginRequest, TokenMfaResponse, TokenResponse, MfaValidateRequest } from "../../models/auth/auth.models";
import {Observable, throwError, of, BehaviorSubject, finalize, shareReplay, filter, take} from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {handleHttpError} from '../../helpers/http-error-response.helper';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly baseUrl = `${environment.backend.url}/api/auth/v1`;
  private readonly userInfoKey = 'WCO-UserInfo';
  private readonly accessTokenKey = 'WCO-AccessToken';
  private readonly refreshTokenKey = 'WCO-RefreshToken';
  private readonly tokenTypeKey = 'WCO-TokenType';
  private readonly tokenExpiresKey = 'WCO-TokenExpires';
  private readonly tokenRefreshExpectedTime = 'WCO-TokenRefreshExpectedTime';
  private readonly refreshTokenExpiresKey = 'WCO-RefreshTokenExpires';
  private readonly refreshTokenExpSecsKey = 'WCO-RefreshTokenExpSecs';
  private readonly challengeTokenKey = 'WCO-ChallengeToken';
  private readonly challengeTokenExpiresKey = 'WCO-ChallengeTokenExpires';

  // Challenge Token expiration time in seconds (10 minutes)
  private readonly challengeTokenExpirationTime = 10 * 60;

  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<TokenResponse | null> = new BehaviorSubject<TokenResponse | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService
  ) { }

  /**
   * Logs in a user with the provided credentials
   * @param recaptchaToken
   * @param username The user's email
   * @param password The user's password
   * @param rememberMe Whether to remember the user
   * @returns An Observable with either TokenMfaResponse (if MFA is required) or TokenResponse (if login is successful)
   */
  public login(recaptchaToken: string, username: string, password: string, rememberMe: boolean = false): Observable<TokenMfaResponse | TokenResponse> {
    const loginRequest: LoginRequest = {
      username,
      password,
      rememberMe
    };

    return this.http.post<TokenMfaResponse | TokenResponse>(`${this.baseUrl}/token`, loginRequest, {
      observe: 'response',
      headers: {
        'x-recaptcha-token': recaptchaToken,
      }
    })
      .pipe(
        map(response => {
          const body = response.body as TokenMfaResponse | TokenResponse;

          // If status is 201, login was successful without MFA
          if (response.status === 201 && 'accessToken' in body) {
            this.handleSuccessfulLogin(body);
          } else if (response.status === 200 && 'challengeToken' in body) {
            // Store the challenge token with expiration time
            this.storeChallengeToken((body as TokenMfaResponse).challengeToken);
          }

          return body;
        }),
        catchError(handleHttpError)
      );
  }

  /**
   * Validates an MFA code
   * @param challengeToken The challenge token received from the login endpoint
   * @param mfaCode The MFA code entered by the user
   * @param recaptchaToken
   * @returns An Observable with TokenResponse
   */
  public validateMfa(challengeToken: string, mfaCode: string, recaptchaToken: string): Observable<TokenResponse> {
    // Check if the challenge token has expired
    if (this.isChallengeTokenExpired()) {
      return throwError(() => new Error('Challenge token has expired. Please login again.'));
    }

    const mfaRequest: MfaValidateRequest = {
      challengeToken,
      mfaCode
    };

    return this.http.post<TokenResponse>(`${this.baseUrl}/2fa/validate`, mfaRequest, {
      headers: {
        'x-recaptcha-token': recaptchaToken,
      }
    })
      .pipe(
        tap(response => {
          // Clear the challenge token after successful validation
          this.clearChallengeToken();
          this.handleSuccessfulLogin(response);
        }),
        catchError(error => {
          // If the error is due to an invalid challenge token, clear it
          if (error.status === 401 && error.error?.message?.includes('challenge token')) {
            this.clearChallengeToken();
          }
          return handleHttpError(error);
        })
      );
  }

  /**
   * Gets the user information
   * @returns An Observable with UserInfo
   */
  public getUserInfo(): Observable<UserInfo> {
    if (!this.isAuthenticated) {
      return throwError(() => new Error('User is not authenticated'));
    }

    // First check if we have the user info in local storage
    const cachedUserInfo = this.userData;
    if (cachedUserInfo) {
      return of(cachedUserInfo);
    }

    // If not, fetch it from the API
    return this.http.get<UserInfo>(`${this.baseUrl}/userinfo`)
      .pipe(
        tap(userInfo => {
          this.localStorageService.set(this.userInfoKey, userInfo, this.refreshTokenExpSecs);
        }),
        catchError(handleHttpError)
      );
  }

  /**
   * Logs out the user by removing all authentication data from local storage
   */
  public logout(): void {
    localStorage.removeItem(this.userInfoKey);
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.tokenTypeKey);
    localStorage.removeItem(this.tokenExpiresKey);
    localStorage.removeItem(this.tokenRefreshExpectedTime);
    localStorage.removeItem(this.refreshTokenExpiresKey);
    localStorage.removeItem(this.refreshTokenExpSecsKey);
    localStorage.removeItem(this.challengeTokenKey);
    localStorage.removeItem(this.challengeTokenExpiresKey);
    this.clearChallengeToken();
  }

  /**
   * Gets the user data from local storage
   */
  public get userData(): UserInfo | undefined {
    return this.localStorageService.get<UserInfo>(this.userInfoKey);
  }

  public get isPigletAllowed(): boolean {
    return this.hasRole(ROLE_PIGLET_LOGIN);
  }

  public hasRole(role: string) {
    return this.userData?.roles?.includes(role) === true;
  }

  public hasAnyRole(roles: string[]) {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Checks if the user is authenticated
   */
  public get isAuthenticated(): boolean {
    return !!this.accessToken || !!this.refreshToken;
  }

  public get isRefreshTokenValid(): boolean {
    return !!this.refreshToken;
  }

  private get refreshTokenExpSecs(): number {
    return this.localStorageService.get<number>(this.refreshTokenExpSecsKey) ?? 0;
  }

  /**
   * Gets the access token from local storage
   */
  public get accessToken(): string | undefined {
    return this.localStorageService.get<string>(this.accessTokenKey);
  }

  public get refreshToken(): string | undefined {
    return this.localStorageService.get<string>(this.refreshTokenKey);
  }

  /**
   * Gets the token type from local storage
   */
  public get tokenType(): string | undefined {
    return this.localStorageService.get<string>(this.tokenTypeKey);
  }

  /**
   * Gets the full authorization header value
   */
  public get authorizationHeader(): string | undefined {
    if (!this.accessToken || !this.tokenType || this.isTokenExpired()) {
      return undefined;
    }
    return `${this.tokenType} ${this.accessToken}`;
  }

  public isRefreshTokenTime(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.tokenRefreshExpectedTime);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() >= expiresAt;
  }

  /**
   * Checks if the token is expired
   */
  public isTokenExpired(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.tokenExpiresKey);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() > expiresAt;
  }

  public isRefreshTokenExpired(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.refreshTokenExpiresKey);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() > expiresAt;
  }

  /**
   * Handles a successful login by storing the token information
   */
  private handleSuccessfulLogin(response: TokenResponse): void {
    const expiresAt = new Date().getTime() + response.expiresIn * 1000;
    const expectedRefreshTime = new Date().getTime() + (response.expiresIn * 0.75) * 1000;

    this.localStorageService.set(this.accessTokenKey, response.accessToken, response.expiresIn);
    this.localStorageService.set(this.tokenTypeKey, response.tokenType, response.expiresIn);
    this.localStorageService.set(this.tokenExpiresKey, expiresAt, response.expiresIn);
    this.localStorageService.set(this.tokenRefreshExpectedTime, expectedRefreshTime, response.expiresIn);

    // decode the refresh token and get exp_secs for refresh expiration time
    const refreshToken = response.refreshToken;
    const refreshTokenParts = refreshToken.split('.');
    const refreshTokenPayload = JSON.parse(atob(refreshTokenParts[1]));
    const refreshTokenExpSecs = refreshTokenPayload.exp_secs;
    // const refreshExpiresAt = new Date().getTime() + refreshTokenPayload.exp * 1000;
    const refreshExpiresAt = refreshTokenPayload.exp * 1000;

    this.localStorageService.set(this.refreshTokenKey, refreshToken, refreshTokenExpSecs);
    this.localStorageService.set(this.refreshTokenExpiresKey, refreshExpiresAt, refreshTokenExpSecs);
    this.localStorageService.set(this.refreshTokenExpSecsKey, refreshTokenExpSecs, refreshTokenExpSecs);

    console.log('expiresAt', expiresAt, new Date(expiresAt));
    console.log('refreshExpiresAt', refreshExpiresAt, new Date(refreshExpiresAt));

    // Fetch and store user info
    this.getUserInfo().subscribe();
  }

  /**
   * Stores the challenge token with its expiration time
   * @param challengeToken The challenge token to store
   */
  private storeChallengeToken(challengeToken: string): void {
    const expiresAt = new Date().getTime() + this.challengeTokenExpirationTime * 1000;

    this.localStorageService.set(this.challengeTokenKey, challengeToken, this.challengeTokenExpirationTime);
    this.localStorageService.set(this.challengeTokenExpiresKey, expiresAt, this.challengeTokenExpirationTime);
  }

  /**
   * Gets the stored challenge token
   * @returns The challenge token or undefined if not found or expired
   */
  public getChallengeToken(): string | undefined {
    if (this.isChallengeTokenExpired()) {
      this.clearChallengeToken();
      return undefined;
    }
    return this.localStorageService.get<string>(this.challengeTokenKey);
  }

  /**
   * Checks if the challenge token has expired
   * @returns True if the token has expired or doesn't exist, false otherwise
   */
  public isChallengeTokenExpired(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.challengeTokenExpiresKey);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() > expiresAt;
  }

  /**
   * Clears the challenge token and its expiration time from storage
   */
  private clearChallengeToken(): void {
    localStorage.removeItem(this.challengeTokenKey);
    localStorage.removeItem(this.challengeTokenExpiresKey);
  }

  public doRefreshToken() {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1)
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.refreshToken;
    return this.http.post<TokenResponse>(`${this.baseUrl}/refresh-token`, { refreshToken })
      .pipe(
        tap(response => {
          this.handleSuccessfulLogin(response);
          this.refreshTokenSubject.next(response);
          this.refreshTokenInProgress = false;
        }),
        catchError(error => {
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(null);
          if (error.status === 401) {
            this.logout();
          }
          return handleHttpError(error);
        }),
        shareReplay(1)
      );
  }
}
